import styled from 'styled-components';
import { cssVars } from '../../../shared/lib/styledTheme';

export const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  height: 100%;
  overflow: hidden;
`;

export const TabsHeaderWrapper = styled.div`
  border-bottom: 1px solid ${cssVars.borderColor};
  background-color: ${cssVars.bgSecondary};
`;

export const TabsHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  
  @media (max-width: 400px) {
    padding: 6px 8px;
    gap: 3px;
  }
`;

export const TabsDropdown = styled.div`
  position: relative;
  display: none;
  
  @media (max-width: 350px) {
    display: block;
    padding: 8px 12px;
    border-bottom: 1px solid ${cssVars.borderColor};
    background-color: ${cssVars.bgSecondary};
  }
`;

export const TabsDropdownButton = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.$active ? cssVars.primary : cssVars.bgColor};
  color: ${props => props.$active ? cssVars.white : cssVars.textPrimary};
  border: 1px solid ${props => props.$active ? cssVars.primary : cssVars.borderColor};
  border-radius: 6px;
  font-size: 13px;
  font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.$active ? cssVars.primaryHover : cssVars.hover};
    border-color: ${cssVars.primary};
  }

  &::after {
    content: '▼';
    font-size: 10px;
    transition: transform 0.2s;
  }
`;

export const TabsDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: ${cssVars.bgColor};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: ${props => props.$isOpen ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.2s ease;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

export const TabsDropdownItem = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  background: none;
  border: none;
  color: ${props => props.$active ? cssVars.primary : cssVars.textPrimary};
  font-size: 13px;
  font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid ${cssVars.borderLight};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${cssVars.hover};
    color: ${cssVars.primary};
  }
`;

export const TabButton = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  border: none;
  background: ${props => props.$active ? cssVars.primary : 'transparent'};
  color: ${props => props.$active ? cssVars.white : cssVars.textSecondary};
  font-size: 11px;
  font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? cssVars.primaryHover : cssVars.hover};
    color: ${props => props.$active ? cssVars.white : cssVars.primary};
  }

  @media (max-width: 400px) {
    padding: 6px 10px;
    font-size: 10px;
    border-radius: 4px;
  }

  @media (max-width: 350px) {
    display: none;
  }
`;

export const TabContent = styled.div`
  padding: 16px 12px;
  min-height: 200px;
  max-height: calc(100vh - 400px);
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  
  @media (max-width: 400px) {
    padding: 12px 8px;
    max-height: calc(100vh - 350px);
  }

  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  scrollbar-width: thin;
  scrollbar-color: ${cssVars.borderColor} transparent;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${cssVars.borderColor};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${cssVars.textSecondary};
  }
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${cssVars.borderLight};
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 400px) {
    flex-direction: column;
    gap: 4px;
    padding: 6px 0;
  }
`;

export const InfoLabel = styled.span`
  font-size: 12px;
  color: ${cssVars.textSecondary};
  font-weight: 500;
  flex-shrink: 0;
  min-width: fit-content;

  @media (max-width: 400px) {
    font-size: 11px;
  }
`;

export const InfoValue = styled.span`
  font-size: 12px;
  color: ${cssVars.textPrimary};
  font-weight: 400;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  @media (max-width: 400px) {
    text-align: left;
    max-width: 100%;
    font-size: 11px;
  }
`;

export const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${cssVars.textSecondary};
  letter-spacing: 0.8px;
  margin: 12px 0 6px 0;
  opacity: 0.8;
  
  &:first-child {
    margin-top: 0;
  }

  @media (max-width: 400px) {
    font-size: 10px;
    margin: 10px 0 4px 0;
  }
`;

export const EmptyState = styled.div`
  padding: 30px 15px;
  text-align: center;
  color: ${cssVars.textTertiary};
  font-size: 13px;

  @media (max-width: 400px) {
    padding: 20px 10px;
    font-size: 12px;
  }
`;

export const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-block;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'error': return '#F44336';
      case 'info': return '#2196F3';
      default: return cssVars.bgSecondary;
    }
  }};
  color: ${props => props.$variant ? '#fff' : cssVars.textPrimary};
  white-space: nowrap;

  @media (max-width: 400px) {
    font-size: 9px;
    padding: 2px 5px;
  }
`;

export const ListItem = styled.div`
  padding: 10px;
  margin-bottom: 8px;
  background-color: ${cssVars.bgColor};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 6px;

  @media (max-width: 400px) {
    padding: 8px;
    margin-bottom: 6px;
  }
`;

export const ListItemTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${cssVars.textPrimary};
  margin-bottom: 6px;
  word-break: break-word;

  @media (max-width: 400px) {
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 10px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 8px;
  }
`;
